// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchJson } from "../infra/fetchJson";

export interface FeatureFlagConfig {
  agent_management: boolean;
  experiments: boolean;
  model_deployments: boolean;
  secrets: boolean;
  pipeline_templates: boolean;
  data_folders: boolean;
}

export const DEFAULT_FLAGS: FeatureFlagConfig = {
  agent_management: false,
  experiments: false,
  model_deployments: true,
  // Mirror the backend FeatureFlagConfig model defaults (workflow/siteconfig/
  // models.py); secrets defaults to True there, so the placeholder/offline
  // fallback must match or RequireFeature briefly hides Secrets on load.
  secrets: true,
  pipeline_templates: false,
  data_folders: true,
};

export const FEATURE_FLAGS_QUERY_KEY = ["feature-flags"];

export const useFeatureFlags = () => {
  return useQuery({
    queryKey: FEATURE_FLAGS_QUERY_KEY,
    queryFn: async (): Promise<FeatureFlagConfig> => {
      try {
        return await fetchJson<FeatureFlagConfig>("/api/feature-flags/");
      } catch {
        return DEFAULT_FLAGS;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: DEFAULT_FLAGS,
  });
};

export const useUpdateFeatureFlags = () => {
  const queryClient = useQueryClient();
  // Uses raw useMutation intentionally — throws in mutationFn for
  // the global QueryErrorHandler to catch, rather than local toast handling.
  return useMutation({
    mutationFn: async (
      config: Partial<FeatureFlagConfig>
    ): Promise<FeatureFlagConfig> => {
      return fetchJson<FeatureFlagConfig>("/api/feature-flags/", {
        method: "PATCH",
        body: JSON.stringify(config),
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(FEATURE_FLAGS_QUERY_KEY, data);
    },
  });
};
